import axios from 'axios'

/**
 * Lista de "filtros já cadastrados" dos editores de automação (gatilho, "por filtro" de Dialog/Valor).
 *
 * São 2 chamadas pesadas em série — `GET /context/web/grid/filtro/` (~430 KB, só para achar os ids dos atributos "filtro" e
 * "nome") e `POST /talk/filter/filtro` com `fetch: 10000` (~650 KB) — e cada componente do editor as refazia a cada
 * montagem: um passo "por filtro" abria as duas, o gatilho abria as duas, e assim por diante (~1 MB por instância). Aqui
 * vão para a store, com TTL, requisição em voo compartilhada e limpeza no logout e quando uma automação é salva (o
 * back cria/atualiza filtros nesse momento).
 */
export const SAVED_FILTERS_TTL_MS = 5 * 60 * 1000

// requisição em voo por origem — duas montagens simultâneas esperam a mesma resposta
const inFlight = {}

/** Converte a resposta do grid de "filtro" em `{ id, label, json }`, achando os atributos pelo nome. */
export function mapSavedFilters (context, grid) {
  const attributes = context.subject.subjectAttributes
  const jsonAttrId = attributes.find((x) => x.subjectAttributeName.toLowerCase() === 'filtro').subjectAttributeId
  const nameAttrId = attributes.find((x) => x.subjectAttributeName.toLowerCase() === 'nome').subjectAttributeId

  return grid.itens.map((item) => ({
    id: item.talkId,
    label: item.talkValues.find((x) => x.subjectAttributeId === nameAttrId).value,
    json: item.talkValues.find((x) => x.subjectAttributeId === jsonAttrId).value
  }))
}

const moduleSavedFilters = {
  state: () => ({
    byOrigin: {}
  }),
  mutations: {
    SET_SAVED_FILTERS (state, { origin, items, at }) {
      state.byOrigin = { ...state.byOrigin, [origin]: { items, at } }
    },
    CLEAR_SAVED_FILTERS (state) {
      state.byOrigin = {}
    }
  },
  actions: {
    /**
     * Devolve a lista (do cache, se tiver menos de 5 min) ou a busca de novo. `force: true` ignora o cache.
     * Falha: lança — quem chama decide (o seletor só fica vazio; a tela segue utilizável).
     */
    async loadSavedFilters ({ state, getters, commit }, { origin = '', force = false } = {}) {
      const cached = state.byOrigin[origin]

      if (!force && cached && Date.now() - cached.at < SAVED_FILTERS_TTL_MS) {
        return cached.items
      }

      if (inFlight[origin]) return inFlight[origin]

      const request = (async () => {
        const { data: context } = await axios.get(`${getters.api}/context/web/grid/filtro/`)

        const { data: grid } = await axios.post(
          `${getters.api}/talk/filter/filtro?origin=${origin}`,
          { fetch: 10000, filterIds: [], isActive: true, page: 1 }
        )

        const items = mapSavedFilters(context, grid)

        commit('SET_SAVED_FILTERS', { origin, items, at: Date.now() })

        return items
      })()

      inFlight[origin] = request

      try {
        return await request
      } finally {
        // só solta o seu próprio registro (um force pode ter começado outra busca no meio)
        if (inFlight[origin] === request) delete inFlight[origin]
      }
    }
  }
}

export default moduleSavedFilters
